import { useCallback, useEffect, useState } from 'react';
import axiosCall from '../../services/axios';
import { useAuthContext } from '../../contexts/AuthContext';
import { LossDraftRepair } from '../../types/LossDraftRepairTypes';

interface WrappedResponse {
  result?: LossDraftRepair[];
  data?: LossDraftRepair[];
}

const useGetLossDraftRepairList = (loanNumber: string) => {
  const { token } = useAuthContext();

  const [lossDraftRepairs, setLossDraftRepairs] = useState<LossDraftRepair[]>([]);
  const [loadingLossDraftRepairs, setLoadingLossDraftRepairs] = useState<boolean>(false);
  const [lossDraftRepairListError, setLossDraftRepairListError] = useState<string>('');

  const getLossDraftRepairList = useCallback(async () => {
    if (!loanNumber || !token) {
      setLossDraftRepairs([]);
      return;
    }

    const endpoint = process.env.REACT_APP_LOSS_DRAFT_REPAIR_LIST;

    if (!endpoint) {
      setLossDraftRepairListError('REACT_APP_LOSS_DRAFT_REPAIR_LIST is not configured.');
      return;
    }

    setLoadingLossDraftRepairs(true);
    setLossDraftRepairListError('');

    try {
      const url = endpoint.replace('?', String(loanNumber));

      const request = {
        url,
        method: 'get',
      };

      const config = {};

      const headers = {
        'Session-Token': token,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      };

      const response = (await axiosCall(request, config, headers)) as LossDraftRepair[] | WrappedResponse;

      if (Array.isArray(response)) {
        setLossDraftRepairs(response);
      } else if (Array.isArray(response?.result)) {
        setLossDraftRepairs(response?.result);
      } else if (Array.isArray(response?.data)) {
        setLossDraftRepairs(response.data);
      } else {
        setLossDraftRepairs([]);
      }
    } catch (error) {
      console.error('Error retrieving Loss Draft Repair list:', error);

      setLossDraftRepairs([]);
      setLossDraftRepairListError('Unable to retrieve Loss Draft Repair records.');
    } finally {
      setLoadingLossDraftRepairs(false);
    }
  }, [loanNumber, token]);

  const createLossDraftRepair = useCallback(
    async (newRepair: Partial<LossDraftRepair>): Promise<boolean> => {
      if (!loanNumber || !token) {
        return false;
      }

      const endpoint = process.env.REACT_APP_CREATE_LOSS_DRAFT_REPAIR;

      if (!endpoint) {
        setLossDraftRepairListError('REACT_APP_LOSS_DRAFT_REPAIR_LOAN_CLAIMID is not configured.');
        return false;
      }

      const request = {
        url: endpoint,
        method: 'post',
        data: newRepair,
      };

      const config = {};

      const headers = {
        'Session-Token': token,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      };

      try {
        await axiosCall(request, config, headers);

        // Refresh the table so the newly-created claim appears immediately
        await getLossDraftRepairList();

        return true;
      } catch (error) {
        console.error('Error creating Loss Draft Repair record:', error);
        setLossDraftRepairListError('Unable to create Loss Draft Repair record.');
        return false;
      }
    },
    [loanNumber, token, getLossDraftRepairList],
  );

  useEffect(() => {
    getLossDraftRepairList();
  }, [getLossDraftRepairList]);

  return {
    lossDraftRepairs,
    loadingLossDraftRepairs,
    lossDraftRepairListError,
    getLossDraftRepairList,
    createLossDraftRepair,
  };
};

export default useGetLossDraftRepairList;
